import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  DocumentTextIcon,
  CogIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  BookOpenIcon,
  AdjustmentsHorizontalIcon,
  PlayIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

export default function YourFabricWizard({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Step 1: Classification Requirements
  const [requirements, setRequirements] = useState({
    description: '',
    supportingFiles: [],
    domain: '',
    useCase: '',
    template: ''
  });
  
  // Step 2: Clarifying Questions
  const [sessionData, setSessionData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [questionsLoading, setQuestionsLoading] = useState(false);
  
  // Step 3: Generated Configuration  
  const [configuration, setConfiguration] = useState(null);
  const [configurationLoading, setConfigurationLoading] = useState(false);

  const domainTemplates = [
    { id: 'healthcare', name: 'Healthcare (HIPAA/PHI)', description: 'Medical records, patient data, healthcare compliance' },
    { id: 'finance', name: 'Financial (SOX/PCI)', description: 'Financial records, payment data, compliance documents' },
    { id: 'legal', name: 'Legal Documents', description: 'Contracts, legal briefs, attorney-client privileged materials' },
    { id: 'hr', name: 'Human Resources', description: 'Employee records, performance reviews, HR policies' },
    { id: 'government', name: 'Government/Security', description: 'Classified documents, FOIA materials, security clearances' },
    { id: 'custom', name: 'Custom Domain', description: 'Define your own classification requirements' }
  ];

  const { getRootProps: getRequirementsProps, getInputProps: getRequirementsInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setRequirements(prev => ({
        ...prev,
        supportingFiles: [...prev.supportingFiles, ...acceptedFiles]
      }));
    },
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    }
  });

  const removeFile = (index) => {
    setRequirements(prev => ({
      ...prev,
      supportingFiles: prev.supportingFiles.filter((_, i) => i !== index)
    }));
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // Validate requirements
      if (!requirements.description.trim() || !requirements.domain) {
        setError('Please provide a classification description and select a domain.');
        return;
      }
      
      // Start with clarifying questions
      await generateClarifyingQuestions();
    } else if (currentStep === 2 && questions.length > 0) {
      // Answer questions step - generate final configuration
      await generateConfiguration();
    }
  };

  const generateClarifyingQuestions = async () => {
    console.log('🔍 Starting generateClarifyingQuestions');
    setQuestionsLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('description', requirements.description);
      formData.append('domain', requirements.domain);
      if (requirements.useCase) formData.append('use_case', requirements.useCase);
      if (requirements.template !== 'custom') formData.append('template', requirements.template);
      
      // Add policy documents
      requirements.supportingFiles.forEach((file) => {
        formData.append('policy_documents', file);
      });
      
      console.log('📡 Calling questions API...');
      const response = await fetch('/api/v1/yourfabric/generate/questions', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.log('❌ Questions API error:', errorData);
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('📋 Questions API result:', result);
      
      if (result.proceed_direct || result.questions.length === 0) {
        console.log('⏭️ Proceeding directly to configuration (proceed_direct:', result.proceed_direct, ', questions length:', result.questions.length, ')');
        // No questions needed, proceed directly to configuration generation
        await generateConfiguration();
      } else {
        console.log('❓ Showing questions step with', result.questions.length, 'questions');
        // Store session data and questions for next step
        setSessionData(result);
        setQuestions(result.questions);
        setCurrentStep(2); // Move to questions step
      }
      
    } catch (err) {
      console.error('Error generating questions:', err);
      setError(`Error generating questions: ${err.message}`);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const generateConfiguration = async () => {
    setConfigurationLoading(true);
    setError(null);
    
    try {
      let result;
      
      if (sessionData && questions.length > 0) {
        // Use interactive flow with answers
        const response = await fetch('/api/v1/yourfabric/generate/finalize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: sessionData.session_id,
            session_data: sessionData,
            answers: answers
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }
        
        result = await response.json();
      } else {
        // Fall back to direct generation
        const formData = new FormData();
        formData.append('description', requirements.description);
        formData.append('domain', requirements.domain);
        if (requirements.useCase) formData.append('use_case', requirements.useCase);
        if (requirements.template !== 'custom') formData.append('template', requirements.template);
        
        requirements.supportingFiles.forEach((file) => {
          formData.append('policy_documents', file);
        });

        const response = await fetch('/api/v1/yourfabric/generate', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to generate configuration');
        }
        
        result = await response.json();
      }
      
      // Set configuration and move to final step
      console.log('Configuration generated:', result.config || result);
      setConfiguration(result.config || result);
      setCurrentStep(questions.length > 0 ? 3 : 2); // Adjust step based on whether questions were shown
      
    } catch (err) {
      console.error('Error generating configuration:', err);
      setError(`Error generating configuration: ${err.message}`);
      setConfiguration(null);
    } finally {
      setConfigurationLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete({
      configurationId: configuration.config_id,
      name: configuration.config_name,
      domain: requirements.domain,
      description: requirements.description
    });
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setQuestions([]);
      setAnswers({});
      setSessionData(null);
    } else if (currentStep === 3) {
      if (questions.length > 0) {
        setCurrentStep(2);
      } else {
        setCurrentStep(1);
      }
      setConfiguration(null);
    }
  };

  const steps = [
    { id: 1, name: 'Define Requirements', icon: BookOpenIcon },
    { id: 2, name: 'Clarifying Questions', icon: AdjustmentsHorizontalIcon },
    { id: 3, name: 'Review Configuration', icon: CogIcon }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress indicator */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center justify-center">
            {steps.map((step, stepIdx) => (
              <li key={step.id} className={`${stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''} relative`}>
                <div className="flex items-center">
                  <div className={`
                    relative flex h-8 w-8 items-center justify-center rounded-full
                    ${currentStep > step.id ? 'bg-blue-600' : currentStep === step.id ? 'border-2 border-blue-600 bg-white' : 'border-2 border-gray-300 bg-white'}
                  `}>
                    {currentStep > step.id ? (
                      <CheckCircleIcon className="h-5 w-5 text-white" />
                    ) : (
                      <step.icon className={`h-5 w-5 ${currentStep === step.id ? 'text-blue-600' : 'text-gray-500'}`} />
                    )}
                  </div>
                  <span className={`ml-4 text-sm font-medium ${currentStep >= step.id ? 'text-blue-600' : 'text-gray-500'}`}>
                    {step.name}
                  </span>
                </div>
                {stepIdx !== steps.length - 1 && (
                  <div className="absolute top-4 left-8 -ml-px h-0.5 w-8 sm:w-20 bg-gray-300" />
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Step 1: Define Requirements */}
      {currentStep === 1 && (
        <div className="space-y-8">
          <div className="text-center">
            <SparklesIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Your Classification System</h2>
            <p className="text-lg text-gray-600">
              Describe what you want to classify and our AI will generate a custom classification system for you
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {/* Classification Description */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">What do you want to classify?</h3>
            <textarea
              value={requirements.description}
              onChange={(e) => setRequirements(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe your classification needs in natural language. For example: 'I want to classify customer support emails by urgency and department', or 'I need to identify sensitive financial information in contracts'..."
              className="w-full h-32 p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Domain Templates */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Choose a domain template (optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {domainTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setRequirements(prev => ({ ...prev, template: template.id, domain: template.name }))}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    requirements.template === template.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h4 className="font-medium text-gray-900">{template.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Use Case (Optional) */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Use Case Context (optional)</h3>
            <input
              type="text"
              value={requirements.useCase}
              onChange={(e) => setRequirements(prev => ({ ...prev, useCase: e.target.value }))}
              placeholder="e.g., 'Regulatory compliance', 'Risk assessment', 'Content moderation'"
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Supporting Documents */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Supporting Documents (optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload policy documents, examples, or guidelines to help the AI understand your requirements better.
            </p>

            <div {...getRequirementsProps()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 cursor-pointer">
              <input {...getRequirementsInputProps()} />
              <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
              </p>
              <p className="text-sm text-gray-500">PDF, DOC, DOCX, TXT files</p>
            </div>

            {requirements.supportingFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {requirements.supportingFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{file.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({Math.round(file.size / 1024)} KB)</span>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              disabled={!requirements.description.trim() || !requirements.domain}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Generate Configuration
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Clarifying Questions */}
      {currentStep === 2 && questions.length > 0 && (
        <div className="space-y-8">
          <div className="text-center">
            <AdjustmentsHorizontalIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Help Us Perfect Your Configuration</h2>
            <p className="text-lg text-gray-600">
              Answer a few questions to ensure we build exactly what you need
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {questionsLoading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Analyzing your requirements...</p>
            </div>
          )}

          {questions.length > 0 && (
            <div className="space-y-6">
              {questions.map((question, index) => (
                <div key={question.id || index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">{question.question}</h3>
                  
                  {question.help_text && (
                    <p className="text-sm text-gray-600 mb-4">{question.help_text}</p>
                  )}
                  
                  {question.options ? (
                    <div className="space-y-2">
                      {question.options.map((option, optIndex) => (
                        <label key={optIndex} className="flex items-start">
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={answers[question.id] === option.value}
                            onChange={(e) => setAnswers(prev => ({...prev, [question.id]: e.target.value}))}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{option.label}</div>
                            {option.description && (
                              <div className="text-sm text-gray-500">{option.description}</div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={answers[question.id] || ''}
                      onChange={(e) => setAnswers(prev => ({...prev, [question.id]: e.target.value}))}
                      placeholder="Please provide your answer..."
                      className="w-full h-24 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between pt-6">
            <button
              onClick={() => setCurrentStep(1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={configurationLoading}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {configurationLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating Configuration...
                </>
              ) : (
                <>
                  Generate Configuration
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review Configuration */}
      {((currentStep === 2 && questions.length === 0) || currentStep === 3) && (
        <div className="space-y-8">
          <div className="text-center">
            <CogIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Your AI-Generated Configuration</h2>
            <p className="text-lg text-gray-600">
              Review and customize your classification system
            </p>
          </div>

          {configurationLoading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">AI is analyzing your requirements and generating configuration...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {configuration && (
            <div className="space-y-6">
              {/* Configuration Overview */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Configuration Name</p>
                    <p className="text-gray-900">{configuration.config_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Domain</p>
                    <p className="text-gray-900">{configuration.domain}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-500">Description</p>
                    <p className="text-gray-900">{configuration.description}</p>
                  </div>
                </div>
              </div>

              {/* Classification Schema */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Classification Categories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {configuration.classification_schema?.map((category, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-md">
                      <h4 className="font-medium text-gray-900">{category.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                      {category.examples && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-500">Examples:</p>
                          <p className="text-xs text-gray-600">{category.examples.join(', ')}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Semantic Bridges */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">AI Reasoning Bridges</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These semantic bridges help the AI understand the conceptual connections for better classification accuracy.
                </p>
                <div className="space-y-3">
                  {configuration.semantic_bridges?.map((bridge, index) => (
                    <div key={index} className="p-3 bg-blue-50 rounded-md">
                      <p className="text-sm text-blue-900">{bridge}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* System Prompts */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">AI System Configuration</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Classification Prompt</p>
                    <div className="mt-1 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-700">
                        {console.log('Displaying classification_prompt:', configuration.classification_prompt)}
                        {configuration.classification_prompt?.substring(0, 200)}...
                      </p>
                    </div>
                  </div>
                  {configuration.semantic_bridges && configuration.semantic_bridges.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Semantic Enhancement Variations</p>
                      <div className="mt-1 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-700">{configuration.semantic_bridges[0]?.substring(0, 200)}...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Back to Requirements
                </button>
                <button
                  onClick={handleComplete}
                  className="flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <PlayIcon className="w-4 h-4 mr-2" />
                  Deploy Configuration
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}